import { z } from 'zod';
import { SiteSubmitReportInput } from './site';

const Id = z.uuid();
const Revision = z.number().int().min(1).max(2_147_483_647);

/** Предметная часть команды. Общий envelope и receipt принадлежат платформе. */
export const SiteRemarkLinkPayload = z.discriminatedUnion('action', [
  z.strictObject({ action: z.literal('link'), reportId: Id, lineId: Id, remarkId: Id }),
  z.strictObject({
    action: z.literal('revoke'),
    reportId: Id,
    lineId: Id,
    linkId: Id,
    expectedLinkRevision: Revision,
    reason: z.string().trim().min(1).max(1000).regex(/\S/),
  }),
]);
export type SiteRemarkLinkPayload = z.infer<typeof SiteRemarkLinkPayload>;

/** Общие поля site-команды; первый выпуск Q7 доступен только онлайн. */
export const SiteRemarkLinkCommand = SiteSubmitReportInput.extend({
  expectedRevision: Revision,
  recordedOffline: z.literal(false),
  payload: SiteRemarkLinkPayload,
});
export type SiteRemarkLinkCommand = z.infer<typeof SiteRemarkLinkCommand>;

/** Состав связи на момент версии отчёта; текущий статус замечания сюда не копируется. */
export const SiteRemarkVersionReference = z.strictObject({
  schemaVersion: z.literal(1),
  reportId: Id,
  reportRevision: Revision,
  lineId: Id,
  linkId: Id,
  linkRevision: Revision,
  remarkId: Id,
});
export type SiteRemarkVersionReference = z.infer<typeof SiteRemarkVersionReference>;

/** Чтение строки всегда явно указывает версию; обратная навигация начинается с remarkId. */
export const SiteRemarkLinkQuery = z.union([
  z.strictObject({ reportId: Id, reportRevision: Revision, lineId: Id }),
  z.strictObject({ remarkId: Id }),
]);
export type SiteRemarkLinkQuery = z.infer<typeof SiteRemarkLinkQuery>;

export const SiteRemarkLinkResult = z.strictObject({
  linkId: Id,
  linkRevision: Revision,
  reportId: Id,
  reportRevision: Revision,
  lineId: Id,
  remarkId: Id,
  revoked: z.boolean(),
});
export type SiteRemarkLinkResult = z.infer<typeof SiteRemarkLinkResult>;

/* ─────────── Чек-листы приёмки (КЧ-04, КЧ-14) ─────────── */

/** Виды работ для шаблонов; неподходящее — «Другое» */
export const CHECKLIST_WORK_KINDS = {
  reinforcement: 'Армирование',
  formwork: 'Опалубка',
  concrete: 'Бетонирование',
  welding: 'Сварка',
  insulation: 'Изоляция',
  backfill: 'Обратная засыпка',
  other: 'Другое',
} as const;
export type ChecklistWorkKind = keyof typeof CHECKLIST_WORK_KINDS;

export const CHECKLIST_ANSWER_TYPES = {
  conformity: 'Соответствует / не соответствует / не применимо',
  number: 'Число с допуском',
  date: 'Дата',
} as const;
export type ChecklistAnswerType = keyof typeof CHECKLIST_ANSWER_TYPES;

export const CHECKLIST_ANSWERS = {
  ok: 'Соответствует',
  fail: 'Не соответствует',
  na: 'Не применимо',
} as const;
export type ChecklistAnswerValue = keyof typeof CHECKLIST_ANSWERS;

/** Источники и виды замечаний, которые может создать чек-лист: из единого реестра раздела 6.10 */
export const CHECKLIST_REMARK_SOURCES = {
  otk: 'ОТК',
  ohs: 'Охрана труда',
  fire_safety: 'Пожарная безопасность',
  ecology: 'Экология',
  builders: 'Строители',
} as const;
export type ChecklistRemarkSource = keyof typeof CHECKLIST_REMARK_SOURCES;

export const CHECKLIST_REMARK_KINDS = {
  remark: 'Замечание',
  prescription: 'Предписание',
} as const;
export type ChecklistRemarkKind = keyof typeof CHECKLIST_REMARK_KINDS;

const Text = (max: number) => z.string().trim().min(1).max(max);
const OptText = (max: number) => z.string().trim().max(max).nullable().optional();

export const ChecklistItemInput = z
  .strictObject({
    text: Text(500),
    answerType: z.enum(
      Object.keys(CHECKLIST_ANSWER_TYPES) as [ChecklistAnswerType, ...ChecklistAnswerType[]],
    ),
    unit: OptText(30),
    minValue: z.number().finite().nullable().optional(),
    maxValue: z.number().finite().nullable().optional(),
    normRef: OptText(300),
  })
  .superRefine((v, ctx) => {
    if (v.answerType === 'number') {
      if (v.minValue == null && v.maxValue == null)
        ctx.addIssue({
          code: 'custom',
          path: ['minValue'],
          message: 'Укажите допуск: от, до или оба значения',
        });
      if (v.minValue != null && v.maxValue != null && v.minValue > v.maxValue)
        ctx.addIssue({ code: 'custom', path: ['minValue'], message: 'Нижняя граница больше верхней' });
    }
  });
export type ChecklistItemInput = z.infer<typeof ChecklistItemInput>;

export const ChecklistTemplateInput = z.strictObject({
  title: Text(200),
  workKind: z.enum(Object.keys(CHECKLIST_WORK_KINDS) as [ChecklistWorkKind, ...ChecklistWorkKind[]]),
  description: OptText(2000),
  remarkSource: z.enum(
    Object.keys(CHECKLIST_REMARK_SOURCES) as [ChecklistRemarkSource, ...ChecklistRemarkSource[]],
  ),
  remarkKind: z.enum(Object.keys(CHECKLIST_REMARK_KINDS) as [ChecklistRemarkKind, ...ChecklistRemarkKind[]]),
  remarkDays: z.number().int().min(1).max(365),
  items: z.array(ChecklistItemInput).min(1).max(100),
});
export type ChecklistTemplateInput = z.infer<typeof ChecklistTemplateInput>;

export interface ChecklistItemView {
  position: number;
  text: string;
  answerType: ChecklistAnswerType;
  unit: string | null;
  minValue: number | null;
  maxValue: number | null;
  normRef: string | null;
}
export interface ChecklistTemplateSummary {
  id: string;
  title: string;
  workKind: ChecklistWorkKind;
  status: 'active' | 'archived';
  revision: number;
  itemCount: number;
  updatedAt: string;
}
export interface ChecklistTemplateDetail extends ChecklistTemplateSummary {
  description: string | null;
  remarkSource: ChecklistRemarkSource;
  remarkKind: ChecklistRemarkKind;
  remarkDays: number;
  items: ChecklistItemView[];
}

export const ChecklistInspectionInput = z.strictObject({
  projectId: z.uuid(),
  objectId: z.uuid().nullable().optional(),
  templateId: z.uuid(),
  place: OptText(300),
  contractorId: z.uuid().nullable().optional(),
  responsibleId: z.uuid(),
  remarkAssigneeId: z.uuid().nullable().optional(),
  dueDate: z.iso.date(),
});
export type ChecklistInspectionInput = z.infer<typeof ChecklistInspectionInput>;

export const ChecklistAnswerInput = z.strictObject({
  answer: z
    .enum(Object.keys(CHECKLIST_ANSWERS) as [ChecklistAnswerValue, ...ChecklistAnswerValue[]])
    .nullable()
    .optional(),
  numberValue: z.number().finite().nullable().optional(),
  dateValue: z.iso.date().nullable().optional(),
  comment: OptText(2000),
});
export type ChecklistAnswerInput = z.infer<typeof ChecklistAnswerInput>;

export const CHECKLIST_STATUSES = {
  in_progress: 'Заполняется',
  completed: 'Завершена',
  cancelled: 'Отменена',
} as const;
export type ChecklistStatus = keyof typeof CHECKLIST_STATUSES;

export function checklistNumber(n: number): string {
  return `ЧЛ-${String(n).padStart(4, '0')}`;
}

/** Число в допуске: границы включаются; пустая граница не ограничивает */
export function inTolerance(value: number, min: number | null, max: number | null): boolean {
  return (min == null || value >= min) && (max == null || value <= max);
}

export interface ChecklistInspectionSummary {
  id: string;
  number: string;
  projectId: string;
  projectCode: string;
  title: string;
  workKind: ChecklistWorkKind;
  place: string | null;
  objectName: string | null;
  contractorName: string | null;
  responsibleName: string;
  dueDate: string;
  overdue: boolean;
  status: ChecklistStatus;
  result: 'ok' | 'with_remarks' | null;
  answered: number;
  total: number;
  failed: number;
}
export interface ChecklistAnswerView extends ChecklistItemView {
  id: string;
  answer: ChecklistAnswerValue | null;
  numberValue: number | null;
  dateValue: string | null;
  comment: string | null;
  answeredByName: string | null;
  answeredAt: string | null;
  remark: { id: string; number: string; status: string } | null;
  photos: Array<{ id: string; name: string }>;
}
export interface ChecklistInspectionDetail extends ChecklistInspectionSummary {
  objectId: string | null;
  completedAt: string | null;
  completedByName: string | null;
  remarkAssigneeName: string | null;
  answers: ChecklistAnswerView[];
  /** Заполнять может исполнитель и тот, у кого есть право вести проверки проекта */
  canAnswer: boolean;
  canComplete: boolean;
}

export interface QualityProjectSummary {
  openRemarks: number;
  overdueRemarks: number;
  openInspections: number;
  overdueInspections: number;
}
